import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

import { useDataStart } from '@/hooks/use-data-start';
import { useOrgSettings } from '@/hooks/use-settings';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/**
 * The one date-range control for every report, drawn exactly like GA4's
 * (user call, 6 Sep 2026: "all date range selectors to match GA4 date range
 * picker, ditto"). Trigger: preset name in grey, the dates beside it, a caret.
 * Popover: GA4's preset list down the left (Custom, Today, Yesterday, This
 * week, Last 7 days, Last week, Last 28 days, Last 30 days, This month, Last
 * month, Last 90 days, Quarter to date, This year, Last calendar year) with a
 * Compare switch at the bottom; Start date / End date boxes and one scrolling
 * calendar on the right; Cancel, and Apply as the one solid button (brand
 * green, like every selected day, since 29 Sep 2026).
 *
 * Two things GA4 does not have to think about, kept from the previous picker:
 *  - the data-start rule. Days before the workspace began recording are not
 *    selectable and presets that reach further back are clamped or disabled.
 *  - `trailingOnly`: a page whose API can express "the last N days" but not a
 *    window that ended in the past. There the calendar picks a START only, the
 *    end stays today, and past-ending presets are disabled rather than
 *    silently answering with a different period than their own label.
 *
 * Selection is STAGED: nothing changes until Apply, per the explicit-save
 * rule. External contract unchanged: {days, range, onDays, onRange}; plus
 * optional {compare, onCompare, compareNote} for pages that can compare.
 *
 * "Last N days" is GA4's and Meta's: the N complete days ENDING YESTERDAY
 * (owner, 27 Oct 2026; providers/report-window `upto`). It used to run to
 * today, so Cookiebees and Meta answered "Last 7 days" for different dates.
 * A page that cannot end before today (`endsToday`: live or trailing-only)
 * offers "Last N days to today" instead, and says so in the trigger.
 */

// The panel: a 208px preset column and a 352px calendar (seven 50px day
// columns with room either side), in CSS pixels.
const PANEL_W = 560;
const PANEL_H = 394;
const dstr = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const parse = (s) => new Date(`${s}T00:00:00`);
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
// GA4 writes "9 Aug 2026" and "9 Aug - 5 Sept 2026".
const fmtDay = (s, withYear = true) => { try { const d = parse(s); return `${d.getDate()} ${MON[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ''}`; } catch { return s; } };
const fmtSpan = (from, to) => {
  if (!from || !to) return '';
  const a = parse(from), b = parse(to);
  if (from === to) return fmtDay(to);
  if (a.getFullYear() === b.getFullYear()) return `${fmtDay(from, false)} - ${fmtDay(to)}`;
  return `${fmtDay(from)} - ${fmtDay(to)}`;
};

/**
 * The WORKSPACE's today, as a local Date at midnight.
 *
 * Every preset here is relative to "now", and the numbers these dates fetch
 * are bucketed in the store's timezone (numbers-canon rule 10). Computing the
 * presets from the browser's clock asks a different question than the one the
 * answer comes back to: an agency operator in London picking "Today" for an
 * Indian store gets London's today, which after 18:30 there is already
 * yesterday for the store. The date is built from the store's wall clock and
 * then carried as an ordinary local Date, so all the arithmetic below and
 * dstr() are unchanged.
 */
function todayIn(zone) {
  const now = new Date();
  if (!zone) { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
  try {
    // en-CA formats as YYYY-MM-DD, which is the only reason it is used here.
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(now).split('-').map(Number);
    return new Date(y, m - 1, d);
  } catch (_e) {
    // An unknown zone must not take the picker down with it.
    const d = new Date(now); d.setHours(0, 0, 0, 0); return d;
  }
}

/** GA4's presets, computed fresh on open. Weeks run Sunday to Saturday as GA4's do. */
function buildPresets(zone, endsToday = false) {
  const now = todayIn(zone);
  const today = dstr(now);
  const ago = (n) => { const d = new Date(now); d.setDate(d.getDate() - n); return dstr(d); };
  const sunday = new Date(now); sunday.setDate(now.getDate() - now.getDay());
  const lastSun = new Date(sunday); lastSun.setDate(sunday.getDate() - 7);
  const lastSat = new Date(sunday); lastSat.setDate(sunday.getDate() - 1);
  const monthFirst = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthFirst = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthLast = new Date(now.getFullYear(), now.getMonth(), 0);
  const qFirst = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const yearFirst = new Date(now.getFullYear(), 0, 1);
  const prevYearFirst = new Date(now.getFullYear() - 1, 0, 1);
  const prevYearLast = new Date(now.getFullYear() - 1, 11, 31);
  // The N complete days ending yesterday; on a page that ends today, the N
  // days to today, named so.
  const lastN = (n) => (endsToday
    ? { l: `Last ${n} days to today`, from: ago(n - 1), to: today, days: n, upto: 'today' }
    : { l: `Last ${n} days`, from: ago(n), to: ago(1), days: n, upto: 'yesterday' });
  return {
    today,
    list: [
      { key: 'custom', l: 'Custom' },
      // Offered only where the page can answer it - see `allowAll`. A browsing
      // surface like Orders means it literally; a report with a comparison
      // period has nothing to compare "everything" against.
      { key: 'all', l: 'All time' },
      { l: 'Today', from: today, to: today, days: 1, upto: 'today' },
      { l: 'Yesterday', from: ago(1), to: ago(1) },
      { l: 'This week (Sun - Today)', from: dstr(sunday), to: today },
      lastN(7),
      { l: 'Last week (Sun - Sat)', from: dstr(lastSun), to: dstr(lastSat) },
      lastN(28),
      lastN(30),
      { l: 'This month', from: dstr(monthFirst), to: today },
      { l: 'Last month', from: dstr(prevMonthFirst), to: dstr(prevMonthLast) },
      lastN(90),
      { l: 'Quarter to date', from: dstr(qFirst), to: today },
      { l: 'This year (Jan - Today)', from: dstr(yearFirst), to: today },
      { l: 'Last calendar year', from: dstr(prevYearFirst), to: dstr(prevYearLast) },
    ],
  };
}

/** Clamp a preset against the data start; one that ends before it is unusable. */
function clampPreset(r, start, today, trailingOnly) {
  if (!r.from) return r;
  let out = r;
  if (start && r.to < start) out = { ...out, disabled: true };
  else if (start && r.from < start) out = { ...out, from: start, clamped: true, days: undefined };
  if (trailingOnly && out.to !== today) out = { ...out, disabled: true };
  return out;
}

/* ─── one month of the scrolling calendar ───────────────────────────────── */
function Month({ year, month, draft, hover, onPick, onHover, minDay, maxDay, today, monthRef }) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;             // Monday-first, as GA4 draws it
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  // While the second click is pending, the hovered day previews the range.
  const from = draft.from, to = draft.to || (draft.pending && hover && hover > draft.from ? hover : draft.from);
  return (
    <div ref={monthRef} className="px-3 pb-1.5">
      <p className="py-1.5 text-[13px] font-semibold text-foreground">{MON[month]} {year}</p>
      <div className="grid grid-cols-7">
        {cells.map((n, i) => {
          if (n == null) return <span key={`x${i}`} className="h-8" />;
          const s = dstr(new Date(year, month, n));
          const off = (minDay && s < minDay) || (maxDay && s > maxDay);
          const isFrom = s === from, isTo = s === to;
          const inRange = from && to && s > from && s < to;
          return (
            <button key={s} type="button" disabled={off}
              onClick={() => onPick(s)} onMouseEnter={() => onHover(s)}
              className={cn('relative mx-auto grid h-8 w-full place-items-center text-[12px] tabular-nums',
                off && 'cursor-default text-muted-foreground/35',
                !off && !isFrom && !isTo && !inRange && 'hover:bg-surface-subtle dark:hover:bg-white/[0.06]',
                inRange && 'bg-primary-soft text-primary dark:bg-blue-500/20 dark:text-blue-200',
                isFrom && from !== to && 'rounded-l-full bg-primary-soft dark:bg-blue-500/20',
                isTo && from !== to && 'rounded-r-full bg-primary-soft dark:bg-blue-500/20')}>
              <span className={cn('grid size-7 place-items-center rounded-full',
                (isFrom || isTo) && 'bg-primary font-semibold text-primary-foreground',
                s === today && !(isFrom || isTo) && 'ring-1 ring-inset ring-primary text-primary dark:text-blue-200')}
>
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── the picker ────────────────────────────────────────────────────────── */
// lead: a small control drawn INSIDE the trigger's box, at its left edge (the
// top bar puts the data-freshness dot there). It is a sibling of the trigger
// button, absolutely placed over its left padding, so the two never nest.
export function DateWindow({ days, range, onDays, onRange, upto = 'yesterday', endsToday = false, trailingOnly = false, alignLeft = false, allowAll = false, compare, onCompare, compareNote, lead = null }) {
  const [open, setOpen] = useState(false);
  // The STAGED selection: label + from/to (+days when it is a plain window),
  // and `pending` while a custom pick is waiting for its second click.
  const [draft, setDraft] = useState(null);
  const [hover, setHover] = useState(null);
  const [focusEnd, setFocusEnd] = useState(false);
  const [draftCompare, setDraftCompare] = useState(!!compare);
  const scrollRef = useRef(null);
  const monthRefs = useRef({});

  const dataStart = useDataStart();
  const start = dataStart?.date || null;
  // The store's zone, from the settings the app already loads. Undefined while
  // it loads, which falls back to the browser's day rather than blocking the
  // picker - the presets rebuild when it arrives.
  const { data: orgSettings } = useOrgSettings();
  const zone = orgSettings?.values?.['org.timezone'] || null;
  const toTodayPage = endsToday || trailingOnly;
  const P = useMemo(() => buildPresets(zone, toTodayPage), [open, zone, toTodayPage]);
  const presets = useMemo(
    () => P.list.filter((r) => r.key !== 'all' || allowAll).map((r) => clampPreset(r, start, P.today, trailingOnly)),
    [P, start, trailingOnly, allowAll]);

  const active = !!(range && range.from && range.to);
  const nDays = Number(days);
  const dated = active || Number.isFinite(nDays);
  // A plain window of N days ends yesterday, unless it was chosen to run to
  // today or this page can only run to today.
  const toToday = toTodayPage || upto === 'today';
  const back = (k) => { const t = new Date(`${P.today}T00:00:00`); t.setDate(t.getDate() - k); return dstr(t); };
  const span = active
    ? { from: range.from, to: range.to }
    // Counted back from the workspace's today, as P.today is: from the
    // browser's clock the label read 6 or 8 days.
    : dated ? (toToday ? { from: back(Math.max(1, nDays) - 1), to: P.today } : { from: back(Math.max(1, nDays)), to: back(1) }) : null;
  const clampedFrom = span && start && span.from < start ? start : span?.from;
  const presetName = active
    ? (presets.find((p) => p.from === range.from && p.to === range.to)?.l || 'Custom')
    : dated ? (presets.find((p) => p.days === nDays && p.upto === (toToday ? 'today' : 'yesterday'))?.l
      || (toToday ? (nDays === 1 ? 'Today' : `Last ${nDays} days to today`) : `Last ${nDays} days`)) : 'All time';
  const label = span ? fmtSpan(clampedFrom, span.to) : 'All time';

  const openWith = () => {
    const cur = active
      ? { l: presetName === 'Custom' ? null : presetName, from: range.from, to: range.to }
      : dated
        ? { l: presetName, days: nDays, upto: toToday ? 'today' : 'yesterday', from: clampedFrom, to: span.to }
        : { l: null, from: start || P.today, to: P.today };
    setDraft(cur); setHover(null); setFocusEnd(false); setDraftCompare(!!compare);
    setOpen(true);
  };
  // Scroll the calendar to the month the draft starts in, once it is drawn.
  useEffect(() => {
    if (!open || !draft?.from) return;
    const key = draft.from.slice(0, 7);
    const el = monthRefs.current[key];
    if (el && scrollRef.current) scrollRef.current.scrollTop = Math.max(0, el.offsetTop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stage = (r) => {
    if (r.disabled) return;
    if (r.key === 'all') { onRange(null); onDays('all'); setOpen(false); return; }
    if (r.key === 'custom') { setDraft((d) => ({ ...(d || {}), l: null, pending: false })); return; }
    setDraft({ l: r.l, days: r.days, upto: r.upto, from: r.from, to: r.to, pending: false });
  };

  // First click starts a range, second completes it; a click before the start
  // starts over. On a trailing-only page a click sets the start and the end
  // stays today.
  const pickDay = (s) => {
    if (trailingOnly) { setDraft({ l: null, from: s, to: P.today, pending: false }); return; }
    if (focusEnd && draft?.from && s >= draft.from) { setDraft({ l: null, from: draft.from, to: s, pending: false }); setFocusEnd(false); return; }
    if (!draft?.pending) { setDraft({ l: null, from: s, to: null, pending: true }); return; }
    if (s < draft.from) { setDraft({ l: null, from: s, to: null, pending: true }); return; }
    setDraft({ l: null, from: draft.from, to: s, pending: false });
  };

  const valid = draft?.from && draft?.to && /^\d{4}-\d{2}-\d{2}$/.test(draft.from) && /^\d{4}-\d{2}-\d{2}$/.test(draft.to)
    && draft.from <= draft.to && !(start && draft.from < start) && draft.to <= P.today;
  const apply = () => {
    if (!valid) return;
    const n = Math.round((parse(draft.to) - parse(draft.from)) / 86400000) + 1;
    // A preset, or dates that are exactly one, stay a rolling window (they move
    // with the calendar); any other dates are a fixed range.
    const same = presets.find((p) => p.days && p.from === draft.from && p.to === draft.to);
    if (draft.days && draft.upto) { onRange(null); onDays(draft.days, draft.upto); }
    else if (same) { onRange(null); onDays(same.days, same.upto); }
    else if (draft.to === P.today && trailingOnly) { onRange(null); onDays(n, 'today'); }
    else onRange({ from: draft.from, to: draft.to });
    if (onCompare && draftCompare !== !!compare) onCompare(draftCompare);
    setOpen(false);
  };

  // Months to draw: from the data start (or 24 months back) to this month.
  const months = useMemo(() => {
    const now = todayIn(zone);
    const firstOf = start ? parse(start) : new Date(now.getFullYear() - 2, now.getMonth(), 1);
    const out = [];
    for (let y = firstOf.getFullYear(), m = firstOf.getMonth(); y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth());) {
      out.push({ y, m });
      m += 1; if (m > 11) { m = 0; y += 1; }
    }
    return out;
  }, [start, open, zone]);
  const hasCompare = typeof onCompare === 'function';

  /* THE PANEL IS FIXED TO THE VIEWPORT, NOT TO THIS BOX.
   *
   * It used to be `absolute` inside the trigger's own relative box, so any
   * ancestor with overflow:hidden cut it off. The top bar is exactly that - it
   * clips on purpose so the centred search cannot collide with the workspace
   * pill - and the result was a 500x394 calendar rendering as a 6px sliver
   * under the header. Clicking the date range appeared to do nothing at all.
   *
   * A popover must never be at the mercy of an ancestor's overflow, so it goes
   * to the body and takes its position from the trigger's viewport rect. That
   * also fixes it inside any page card that clips, which is the other place
   * this control is used. */
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = anchorRef.current;
    const a = el?.getBoundingClientRect();
    if (!a) return;
    // THE PAGE IS ZOOMED ON A WIDE SCREEN (App.css: html { zoom: 1.06 } from
    // 1920px). The trigger's rect comes back in screen pixels, but a `left`
    // written on the panel is in CSS pixels and gets zoomed again - so at 2000
    // wide the right-anchored panel landed 6% further right than asked and its
    // Saturday column and Apply button ran off the window. Measure the zoom off
    // the trigger itself (screen width over layout width) and do the sums in
    // screen pixels, converting once at the end.
    const k = el.offsetWidth ? a.width / el.offsetWidth : 1;
    const Wv = PANEL_W * k, Hv = PANEL_H * k, gap = 6 * k, edge = 12;
    // Right-aligned to the trigger by default, which is where the reporting
    // window lives, so it opens inward rather than off the screen edge.
    const leftV = Math.min(Math.max(edge, alignLeft ? a.left : a.right - Wv), Math.max(edge, window.innerWidth - Wv - edge));
    const below = a.bottom + gap;
    // Flip above when there is no room under it.
    const topV = below + Hv > window.innerHeight - edge ? Math.max(edge, a.top - Hv - gap) : below;
    setPos({ left: leftV / k, top: topV / k, maxHeight: (window.innerHeight - topV - edge) / k });
  }, [alignLeft]);
  useEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <div className="relative">
      {lead && <span className="absolute left-1 top-1/2 z-10 -translate-y-1/2">{lead}</span>}
      {/* GA4's trigger: the preset in grey, the dates beside it, a caret.
          Inside a box, because the reporting window governs every number on the
          page and a borderless row of text read as a label rather than the
          control it is - people could not see there was anything to click. */}
      <button ref={anchorRef} type="button" onClick={() => (open ? setOpen(false) : openWith())}
        aria-label={`Reporting window: ${presetName}, ${label}`}
        className={cn('inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-input bg-surface-subtle px-2 text-[13px] text-foreground sm:gap-2 sm:px-2.5 focus-visible:ring-2 focus-visible:ring-ring', lead && 'pl-8 sm:pl-8', ' outline-none transition-colors hover:border-border-strong hover:bg-surface-muted dark:hover:bg-white/[0.06]', open && 'border-primary/60 bg-surface-subtle ring-2 ring-primary/15 dark:bg-white/[0.06]')}>
        {/* Qbee open beside the page (data-qbee on <html>) leaves the top bar
            a phone's room on a wide screen: the phone's short form then. */}
        <span className="hidden text-[13px] text-muted-foreground sm:inline lg:[html[data-qbee=open]_&]:hidden">{presetName}</span>
        {/* On a phone the year goes: the bar has room for the dates or the
            avatar, and the year is the part a reader already knows. */}
        <span className="hidden font-medium tabular-nums sm:inline lg:[html[data-qbee=open]_&]:hidden">{label}</span>
        <span className="font-medium tabular-nums sm:hidden lg:[html[data-qbee=open]_&]:inline">{span ? fmtSpan(clampedFrom, span.to).replace(/ \d{4}$/, '').replace(/ \d{4} -/, ' -') : label}</span>
        <ChevronDown className="size-4 text-muted-foreground" />
      </button>

      {open && createPortal((
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div style={{ width: PANEL_W, ...(pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight } : { opacity: 0 }) }}
            className="fixed z-[61] flex overflow-hidden rounded-xl border border-border bg-popover text-foreground shadow-modal">
            {/* the preset list. Wide enough that "This week (Sun - Today)" and
                "This year (Jan - Today)" hold one line each. */}
            <div className="w-[208px] shrink-0 border-r border-border py-1.5">
              <div className="max-h-[300px] overflow-y-auto">
                {presets.map((r) => {
                  const selected = r.key === 'custom' ? draft && !draft.l : draft?.l === r.l;
                  return (
                    <button key={r.key || r.l} type="button" disabled={r.disabled} onClick={() => stage(r)}
                      title={r.disabled ? (trailingOnly ? 'This report measures a window ending today' : 'Before this workspace began recording') : r.clamped ? `Clamped to ${fmtDay(r.from)}, when recording began` : undefined}
                      className={cn('flex w-full items-center whitespace-nowrap px-3.5 py-[5px] text-left text-[13px] transition-colors',
                        r.disabled ? 'cursor-default text-muted-foreground/40'
                          : selected ? 'bg-primary-soft font-medium text-primary dark:bg-blue-500/20 dark:text-blue-200'
                            : 'hover:bg-surface-subtle dark:hover:bg-white/[0.06]')}>
                      {r.l}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-border px-3.5 pt-2 pb-1">
                <span className={cn('text-[13px]', !hasCompare && 'text-muted-foreground')}>Compare</span>
                <Switch checked={hasCompare && draftCompare} onCheckedChange={setDraftCompare} disabled={!hasCompare} />
              </div>
              {(compareNote || !hasCompare) && (
                <p className="px-3.5 pb-1.5 text-[12px] leading-snug text-muted-foreground">{compareNote || 'Not available on this report.'}</p>
              )}
            </div>

            {/* dates and the scrolling calendar */}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex gap-2 px-3 pt-3 pb-1.5">
                {[['Start date', 'from', false], ['End date', 'to', true]].map(([lbl, k, isEnd]) => (
                  <button key={k} type="button" onClick={() => setFocusEnd(isEnd)} disabled={isEnd && trailingOnly}
                    className={cn('flex-1 rounded border px-2.5 py-1 text-left transition-colors',
                      focusEnd === isEnd ? 'border-primary ring-1 ring-primary' : 'border-border',
                      isEnd && trailingOnly && 'opacity-60')}>
                    <span className={cn('block text-[12px]', focusEnd === isEnd ? 'text-primary dark:text-blue-200' : 'text-muted-foreground')}>{lbl}</span>
                    <span className="block text-[13px] tabular-nums">{draft?.[k] ? fmtDay(draft[k]) : <span className="text-muted-foreground">Pick a day</span>}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-7 border-b border-border px-3 pb-1 text-center text-[12px] text-muted-foreground">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}
              </div>
              <div ref={scrollRef} className="relative h-[224px] overflow-y-auto">
                {months.map(({ y, m }) => (
                  <Month key={`${y}-${m}`} year={y} month={m} today={P.today}
                    monthRef={(el) => { monthRefs.current[`${y}-${String(m + 1).padStart(2, '0')}`] = el; }}
                    draft={draft || {}} hover={hover} onPick={pickDay} onHover={setHover}
                    minDay={start} maxDay={P.today} />
                ))}
                {/* Room under this month, so it can scroll to the top of the
                    pane instead of showing the last row of the month before. */}
                <div className="h-12" aria-hidden />
              </div>
              <div className="flex items-center justify-between border-t border-border px-3 py-2">
                <span className="text-[12px] leading-snug text-muted-foreground">
                  {trailingOnly ? 'This report measures a window ending today.' : start ? `Recording began ${fmtDay(start)}.` : ''}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setOpen(false)}
                    className="h-8 rounded-md px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-surface-subtle dark:hover:bg-white/[0.06]">Cancel</button>
                  <button type="button" onClick={apply} disabled={!valid}
                    className="h-8 rounded-md bg-primary px-3.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-brand-300">Apply</button>
                </div>
              </div>
            </div>
          </div>
        </>
      ), document.body)}
    </div>
  );
}
